<script>
  import { workOrderStore } from '../../stores/work-order';
  import { projectStore } from '../../stores/project';
  import { clientStore } from '../../stores/client';
  import { worker } from '../../stores/worker';

  $: workOrders = $workOrderStore.items || [];
  $: projects = $projectStore.items || [];
  $: clients = $clientStore.items || [];
  $: workers = $worker.workers || [];
  $: loading = $workOrderStore.loading;

  // Key metrics
  $: activeWOs = workOrders.filter(wo => wo.status === 'IN_PROGRESS');
  $: pendingWOs = workOrders.filter(wo => wo.status === 'PENDING');
  $: urgentWOs = workOrders.filter(wo => wo.priority === 'URGENT' && wo.status !== 'COMPLETED');
  $: completedToday = workOrders.filter(wo => {
    if (wo.status !== 'COMPLETED') return false;
    const today = new Date().toDateString();
    return wo.completed_at && new Date(wo.completed_at).toDateString() === today;
  });

  $: activeProjects = projects.filter(p => p.status === 'IN_PROGRESS');
  $: availableWorkers = workers.filter(w => w.availability_status === 'AVAILABLE');

  $: totalHoursThisWeek = workOrders.reduce((sum, wo) => sum + (wo.actual_hours || 0), 0);
  $: totalBudget = projects.reduce((sum, p) => sum + (p.budget || 0), 0);

  function getProject(projectId) {
    return projects.find(p => p.id === projectId);
  }

  function getClient(clientId) {
    return clients.find(c => c.id === clientId);
  }

  function statusConfig(status) {
    if (status === 'COMPLETED') return { label: 'Done', class: 'status-success' };
    if (status === 'IN_PROGRESS') return { label: 'Active', class: 'status-active' };
    if (status === 'PENDING') return { label: 'Pending', class: 'status-warning' };
    return { label: status || 'Unknown', class: 'status-default' };
  }

  function priorityConfig(priority) {
    if (priority === 'URGENT') return { label: 'Urgent', class: 'priority-urgent' };
    if (priority === 'HIGH') return { label: 'High', class: 'priority-high' };
    return null;
  }

  function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
  }
</script>

<div class="dashboard">
  <!-- Mission Control Stats -->
  <div class="stats-grid">
    <div class="stat-card urgent" class:has-items={urgentWOs.length > 0}>
      <div class="stat-header">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span class="stat-label">Urgent</span>
      </div>
      <span class="stat-value">{urgentWOs.length}</span>
    </div>

    <div class="stat-card">
      <div class="stat-header">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <span class="stat-label">In Progress</span>
      </div>
      <span class="stat-value">{activeWOs.length}</span>
    </div>

    <div class="stat-card">
      <div class="stat-header">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="stat-label">Pending</span>
      </div>
      <span class="stat-value">{pendingWOs.length}</span>
    </div>

    <div class="stat-card">
      <div class="stat-header">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span class="stat-label">Available</span>
      </div>
      <span class="stat-value">{availableWorkers.length}</span>
    </div>
  </div>

  <!-- Main Content Grid -->
  <div class="content-grid">
    <!-- Urgent & Active Tasks -->
    <div class="panel">
      <div class="panel-header">
        <h3>Active Tasks</h3>
        <span class="panel-count">{activeWOs.length + pendingWOs.length}</span>
      </div>

      {#if loading}
        <div class="loading-state">
          <div class="spinner"></div>
        </div>
      {:else if activeWOs.length === 0 && pendingWOs.length === 0}
        <div class="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>All caught up</p>
        </div>
      {:else}
        <div class="task-list">
          {#each [...urgentWOs, ...activeWOs.filter(wo => wo.priority !== 'URGENT'), ...pendingWOs].slice(0, 8) as wo}
            {@const project = getProject(wo.project)}
            {@const status = statusConfig(wo.status)}
            {@const priority = priorityConfig(wo.priority)}
            <div class="task-item" class:urgent={wo.priority === 'URGENT'}>
              <div class="task-main">
                <div class="task-title-row">
                  <span class="task-title">{wo.title}</span>
                  {#if priority}
                    <span class="priority-badge {priority.class}">{priority.label}</span>
                  {/if}
                </div>
                <div class="task-meta">
                  {#if project}
                    <span class="project-name">{project.name}</span>
                  {/if}
                  <span class="status-badge {status.class}">{status.label}</span>
                </div>
              </div>
              <div class="task-hours">
                <span class="hours-current">{wo.actual_hours || 0}</span>
                <span class="hours-separator">/</span>
                <span class="hours-total">{wo.estimated_hours || 0}h</span>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>

    <!-- Project Health -->
    <div class="panel">
      <div class="panel-header">
        <h3>Project Health</h3>
        <span class="panel-count">{activeProjects.length} active</span>
      </div>

      {#if activeProjects.length === 0}
        <div class="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p>No active projects</p>
        </div>
      {:else}
        <div class="project-list">
          {#each activeProjects.slice(0, 6) as project}
            {@const client = getClient(project.client)}
            {@const projectWOs = workOrders.filter(wo => wo.project === project.id)}
            {@const activeTaskCount = projectWOs.filter(wo => wo.status !== 'COMPLETED').length}
            <div class="project-item">
              <div class="project-info">
                <span class="project-name">{project.name}</span>
                <span class="client-name">{client?.name || 'No client'}</span>
              </div>
              <div class="project-stats">
                {#if activeTaskCount > 0}
                  <span class="task-badge">{activeTaskCount} task{activeTaskCount !== 1 ? 's' : ''}</span>
                {/if}
                <div class="progress-group">
                  <div class="progress-bar">
                    <div class="progress-fill" style="width: {project.progress || 0}%"></div>
                  </div>
                  <span class="progress-text">{project.progress || 0}%</span>
                </div>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>

  <!-- Quick Metrics Row -->
  <div class="metrics-row">
    <div class="metric-card">
      <span class="metric-label">Active Projects</span>
      <span class="metric-value">{activeProjects.length}</span>
    </div>
    <div class="metric-card">
      <span class="metric-label">Total Hours</span>
      <span class="metric-value">{totalHoursThisWeek}h</span>
    </div>
    <div class="metric-card">
      <span class="metric-label">Portfolio Value</span>
      <span class="metric-value">{formatCurrency(totalBudget)}</span>
    </div>
    <div class="metric-card">
      <span class="metric-label">Completed Today</span>
      <span class="metric-value">{completedToday.length}</span>
    </div>
  </div>
</div>

<style>
  .dashboard {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  .stat-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .stat-card.urgent.has-items {
    border-color: #fca5a5;
    background: #fef2f2;
  }

  .stat-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .stat-header svg {
    width: 16px;
    height: 16px;
    color: var(--text-muted);
  }

  .stat-card.urgent.has-items .stat-header svg {
    color: #dc2626;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  .stat-card.urgent.has-items .stat-label {
    color: #991b1b;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: var(--text);
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  .stat-card.urgent.has-items .stat-value {
    color: #dc2626;
  }

  /* Content Grid */
  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  /* Panel */
  .panel {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border);
  }

  .panel-header h3 {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    margin: 0;
  }

  .panel-count {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-muted);
    background: var(--bg);
    padding: 3px 8px;
    border-radius: 4px;
  }

  /* Loading & Empty States */
  .loading-state, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    color: var(--text-muted);
  }

  .empty-state svg {
    width: 36px;
    height: 36px;
    margin-bottom: 12px;
    opacity: 0.3;
  }

  .empty-state p {
    font-size: 13px;
    margin: 0;
  }

  /* Task List */
  .task-list {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .task-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: 4px;
    gap: 12px;
    transition: background 100ms;
  }

  .task-item:hover {
    background: var(--bg);
  }

  .task-item.urgent {
    background: #fef2f2;
  }

  .task-item.urgent:hover {
    background: #fee2e2;
  }

  .task-main {
    flex: 1;
    min-width: 0;
  }

  .task-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .task-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .task-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 3px;
  }

  .project-name {
    font-size: 11px;
    color: var(--text-muted);
  }

  .status-badge {
    font-size: 9px;
    font-weight: 600;
    padding: 2px 5px;
    border-radius: 3px;
    text-transform: uppercase;
  }

  .status-success { background: #dcfce7; color: #166534; }
  .status-active { background: #dbeafe; color: #1e40af; }
  .status-warning { background: #fef3c7; color: #92400e; }
  .status-default { background: #f1f5f9; color: #475569; }

  .priority-badge {
    font-size: 9px;
    font-weight: 600;
    padding: 2px 5px;
    border-radius: 3px;
    text-transform: uppercase;
  }

  .priority-urgent { background: #fee2e2; color: #991b1b; }
  .priority-high { background: #fef3c7; color: #92400e; }

  .task-hours {
    display: flex;
    align-items: baseline;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .hours-current {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
  }

  .hours-separator {
    font-size: 12px;
    color: var(--text-muted);
    margin: 0 2px;
  }

  .hours-total {
    font-size: 12px;
    color: var(--text-muted);
  }

  /* Project List */
  .project-list {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .project-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: 4px;
    gap: 12px;
    transition: background 100ms;
  }

  .project-item:hover {
    background: var(--bg);
  }

  .project-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .project-info .project-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .client-name {
    font-size: 11px;
    color: var(--text-muted);
  }

  .project-stats {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .task-badge {
    font-size: 10px;
    font-weight: 500;
    color: var(--accent);
  }

  .progress-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .progress-bar {
    width: 60px;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--accent);
    border-radius: 2px;
    transition: width 200ms;
  }

  .progress-text {
    font-size: 11px;
    font-weight: 500;
    color: var(--text-muted);
    min-width: 28px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  /* Metrics Row */
  .metrics-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  .metric-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .metric-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  .metric-value {
    font-size: 18px;
    font-weight: 600;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }

  /* Mobile Responsive */
  @media (max-width: 1023px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .content-grid {
      grid-template-columns: 1fr;
    }

    .metrics-row {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 767px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .stat-value {
      font-size: 22px;
    }

    .metrics-row {
      grid-template-columns: repeat(2, 1fr);
    }

    .metric-value {
      font-size: 16px;
    }

    .project-stats {
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
    }
  }
</style>
